import { SkillCastPattern } from "game/data/skills/skill-patterns/index.js";
import { ReactiveTriggerConfig } from "./events";
import { PokeNames } from "types/pokemon/poke-names";

export interface Position {
  x: number;
  y: number;
}

/**
 * Configuration for battle grid dimensions and walkable areas
 */
export interface GridConfig {
  /** Array of positions that are walkable (all others are considered obstacles) */
  walkablePositions: Position[];
}

export enum PokemonType {
  NORMAL = `NORMAL`,
  FIRE = `FIRE`,
  WATER = `WATER`,
  GRASS = `GRASS`,
  ELECTRIC = `ELECTRIC`,
  ICE = `ICE`,
  PSYCHIC = `PSYCHIC`,
  FIGHTING = `FIGHTING`,
  POISON = `POISON`,
  GROUND = `GROUND`,
  FLYING = `FLYING`,
  BUG = `BUG`,
  ROCK = `ROCK`,
  GHOST = `GHOST`,
  DRAGON = `DRAGON`,
  DARK = `DARK`,
  STEEL = `STEEL`,
  FAIRY = `FAIRY`,
}

export const PokemonTypeArray = Object.values(PokemonType);

export enum StatusEffect {
  BURN = `BURN`, // Permanent, stacks, damage on each damage received
  POISON = `POISON`, // Permanent, stacks, damage on each damage received
  STUN = `STUN`, //
  SLEEP = `SLEEP`, // Permanent, cant act, expire when receive damage
  KNOCKUP = `KNOCKUP`, // Like STUN but with float visual
  SPEED_UP = `SPEED_UP`, // Permanent,
  SPEED_DOWN = `SPEED_DOWN`, // Permanent
  ATTACK_UP = `ATTACK_UP`, // Permanent, expire when dealing dmg. +30% dmg
  ATTACK_DOWN = `ATTACK_DOWN`, // Permanent, expire when dealing dmg. -30% dmg
  DEFENSE_UP = `DEFENSE_UP`, // Permanent, expire when receive dmg. -50% incoming dmg
  DEFENSE_DOWN = `DEFENSE_DOWN`, // Permanent, expire when receive dmg. +50% incoming dmg
  CRITICAL_UP = `CRITICAL_UP`, // Permanent, next damage is 100% critical dmg (2x damage)
  FEAR = `FEAR`, // Permanent, 50% chance to miss skill, expire when use skill
  BLIND = `BLIND`, // Same as FEAR
  FREEZE = `FREEZE`, // Permanent, can act but not move, expire when receive damage
  CONFUSION = `CONFUSION`, //
  TETHERED = `TETHERED`, // Indicates pokemon is part of a tether
}

export enum SkillType {
  DAMAGE = `DAMAGE`,
  GAP_CLOSE = `GAP_CLOSE`,
  BUFF = `BUFF`,
  DEBUFF = `DEBUFF`,
  HEAL = `HEAL`,
  SHIELD = `SHIELD`,
  SELF_HEAL = `SELF_HEAL`,
  SELF_SHIELD = `SELF_SHIELD`,
  EVADE = `EVADE`,
}

export enum SkillRangeType {
  POINT_TARGET = `POINT_TARGET`, // Simple -- Direct melee, or point-target ranged
  ALL_ALLIES = `ALL_ALLIES`,
  ALL_ENEMIES = `ALL_ENEMIES`,
  TARGETED_INVOKE = `TARGETED_INVOKE`,
  ANCHORED_INVOKE = `ANCHORED_INVOKE`,
  SELF = `SELF`,
}

export interface SkillStatusEffect {
  status: StatusEffect;
  duration_ticks: number;
  intensity: number;
}

/**
 * Active status effect instance on a Pokemon
 */
export interface StatusEffectInstance {
  status: StatusEffect;
  intensity: number;
  durationTicks: number;
  sourceId?: number; // Which Pokemon/skill applied this
  appliedAtTick: number;
  lastDamageAtTick?: number; // Track when DoT last applied damage
}

export interface SkillEvent {
  __typename:
  | `MeleeHit`
  | `GapClose`
  | `ProjectileCast`
  | `Heal`
  | `Shield`
  | `AnchoredInvoke`
  | `Vfx`
  | `Wait`
  | `ParticleEmission`
  | `Global`
  | `Cleanse`
  | `StealBuff`
  | `CooldownSkip`
  | `Tether`
  | `Pull`;
  duration_ticks: number;
  blocks_event_pipeline?: boolean;
  target: {
    type: "self" | "ally" | "enemy";
    id: number;
  };
  status_effects: SkillStatusEffect[];
  on_complete?: () => void;
  // Logs -- Populated by the server during battle simulation
  starting_tick: number;
  battle_log?: Record<string, any>;
  prepared_on_hit_events?: SkillEvent[]; // Prepared on_hit events with populated target ids (set by server)
}

export interface SkillEvent_MeleeHit extends SkillEvent {
  __typename: "MeleeHit";
  damage: number;
  result_hp?: number; // Target's HP after damage (set by server)
  self_damage?: number; // Recoil damage to caster
  self_status_effects?: SkillStatusEffect[]; // Status effects to apply to self
}

export enum GapCloseType {
  TARGET_FURTHEST = `TARGET_FURTHEST`,
  TARGET_LOW_HP = `TARGET_LOW_HP`,
  TARGET_NEAREST = `TARGET_NEAREST`,
}

export interface SkillEvent_GapClose extends SkillEvent {
  __typename: "GapClose";
  damage: number;
  result_hp?: number; // Target's HP after damage (set by server)
  range: number;
  gap_close_type:
  | GapCloseType.TARGET_FURTHEST
  | GapCloseType.TARGET_LOW_HP
  | GapCloseType.TARGET_NEAREST;
  battle_log?: {
    target_position: Position;
  };
}

export interface SkillEvent_ProjectileCast extends SkillEvent {
  __typename: "ProjectileCast";
  sprite_path: string;
  sprite_scale?: number;
  speed: number;
  damage: number;
  result_hp?: number; // Target's HP after damage (set by server)
  battle_log?: {
    impact_tick: number;
  };
  self_damage?: number; // Recoil damage to caster
  self_status_effects?: SkillStatusEffect[]; // Status effects to apply to self
}

export interface SkillEvent_AnchoredInvoke extends SkillEvent {
  __typename: "AnchoredInvoke";
  relative_x: number;
  relative_y: number;
  should_align_to_user_dir?: boolean;
  sprite_path?: string;
  sprite_scale?: number;
  sprite_scale_x?: number;
  sprite_scale_y?: number;
  anim_loops?: number;
  anim_speed?: number;
  result_hp?: number; // Target's HP after damage (set by server)
  damage?: number;
  healing?: number;
}

export interface SkillEvent_ParticleEmission extends SkillEvent {
  __typename: "ParticleEmission";
  should_align_to_destination?: boolean;
  should_align_pattern_to_user_dir?: boolean;
  particle_count: number;
  particle_speed: number;
  max_spawn_delay_ms?: number;
  pattern: ReturnType<SkillCastPattern>;
  sprite_path: string;
  sprite_scale?: number;
  anim_loops?: number;
  anim_speed?: number;
}

export interface SkillEvent_Global extends SkillEvent {
  __typename: "Global";
  damage?: number;
  healing?: number;
  effect_delay_ticks?: number;
  vfx?: {
    sprite_path: string;
    sprite_scale?: number;
    sprite_scale_x?: number;
    sprite_scale_y?: number;
    sprite_alpha?: number;
    loops?: number;
    speed?: number;
    anchor_to_target?: boolean;
    y_offset?: number;
    falling?: boolean;
  };
  probability?: number; // 0 - 1.0; Default is 1.0;
  event_check?: SkillGlobalEventCheck;
  successful_target_ids?: string[];
  self_damage?: number; // Recoil damage to caster
  self_status_effects?: SkillStatusEffect[]; // Status effects to apply to self
}

export interface SkillEvent_Heal extends SkillEvent {
  __typename: "Heal";
  heal_amount: number;
}

export interface SkillEvent_Shield extends SkillEvent {
  __typename: "Shield";
  shield_amount: number;
  shield_duration_ticks: number;
}

export interface SkillEvent_Vfx extends SkillEvent {
  __typename: "Vfx";
  sprite_path: string;
  sprite_scale?: number;
  sprite_scale_x?: number;
  sprite_scale_y?: number;
  sprite_alpha?: number;
  loops?: number;
  speed?: number;
  ping_pong?: boolean;
  anchor_to_target?: boolean;
  y_offset?: number;
  falling?: boolean;
  damage?: number; // Optional damage to apply to target
  result_hp?: number; // Target's HP after damage (set by server)
  self_damage?: number; // Recoil damage to caster
  self_status_effects?: SkillStatusEffect[]; // Status effects to apply to self
}

export interface SkillEvent_Wait extends SkillEvent {
  __typename: "Wait";
}

export interface SkillEvent_Cleanse extends SkillEvent {
  __typename: "Cleanse";
  cleanse_count: number; // How many debuffs to remove (0 = all)
  cleanse_types?: StatusEffect[]; // Specific debuffs to target (optional)
}

export interface SkillEvent_StealBuff extends SkillEvent {
  __typename: "StealBuff";
  steal_count: number; // How many buffs to steal (0 = all)
  steal_types?: StatusEffect[]; // Specific buffs to target (optional)
  heal_on_steal?: number; // Optional heal amount when stealing
  stolen_buffs?: SkillStatusEffect[]; // Populated by server with actually stolen buffs
}

export interface SkillEvent_CooldownSkip extends SkillEvent {
  __typename: "CooldownSkip";
  skip_ticks: number; // How many ticks to skip off cooldowns
  target_scope: "self" | "ally" | "all_allies";
}

export interface TetherEffects {
  periodicHeal?: number; // Heal amount per tick_interval
  periodicDamage?: number; // Damage amount per tick_interval (self-damage for caster)
  periodicStatus?: SkillStatusEffect; // Status effect to apply per tick_interval
}

export interface SkillEvent_Tether extends SkillEvent {
  __typename: "Tether";
  tether_type: "ally" | "enemy";
  max_distance: number; // Grid tiles before break
  tick_interval: number; // Apply effects every X ticks

  // Separate effects for caster and target
  caster_effects?: TetherEffects; // Effects applied to the caster
  target_effects?: TetherEffects; // Effects applied to the tethered target

  // Legacy fields (deprecated - use caster_effects/target_effects instead)
  /** @deprecated Use target_effects.periodic_heal instead */
  periodic_heal?: number;
  /** @deprecated Use target_effects.periodic_status instead */
  periodic_status?: SkillStatusEffect;

  tether_vfx?: { sprite_path: string };
  tether_line_config?: {
    enabled?: boolean; // Whether to draw tether line (default: true)
    color?: number; // Line color as hex (default: green for ally, red for enemy)
    width?: number; // Line width in pixels (default: 3)
    glow?: boolean; // Add glowing effect (default: true)
    pulse?: boolean; // Pulsing animation (default: true)
    energy_flow?: boolean; // Animated particles flowing along line (default: false)
  };
  // Battle log - populated by server
  battle_log?: {
    tether_target_id: number;
    break_tick?: number;
  };
}

export interface SkillEvent_Pull extends SkillEvent {
  __typename: "Pull";
  pull_scope: "single" | "all_enemies";
  stun_ticks?: number; // Optional stun after pull
  damage?: number; // Optional damage on pull
  pull_vfx?: { sprite_path: string; speed: number };
  hook_line_config?: {
    enabled?: boolean; // Whether to draw hook line (default: true)
    color?: number; // Line color as hex (default: 0xff6b35)
    width?: number; // Line width in pixels (default: 6)
  };
  // Battle log - populated by server
  battle_log?: {
    pulled_targets: Array<{
      id: number;
      from: Position;
      to: Position;
    }>;
  };
}

export interface SkillData {
  id: string;
  name: string;
  description: string;
  exclusive_of?: PokeNames[];
  power: number;
  pokemon_type: PokemonType;
  types: SkillType[];
  range: {
    type: SkillRangeType;
    value: number;
  };
  cooldown_ticks: number;
  mana_cost: number;
  on_cast: SkillEvent[];
  on_hit: SkillEvent[];
  align_to_user_dir?: boolean;
  cast_pattern?: ReturnType<SkillCastPattern>;
  charging?: {
    is_required: boolean;
    duration_ticks: number;
    align_to: "south" | "target";
    anim_on_end?: {
      name: "rotate" | `melee` | `special` | `idle` | "pose";
      dir: "south" | "target";
    };
    // Channeling options
    is_channel?: boolean; // true = channeling (effect during), false = charging (effect after)
    interruptible?: boolean; // Can be interrupted by damage
    /**
     * @description Status to apply to target during channel
     * @example { status: StatusEffect.KNOCKUP, duration_ticks: 10, intensity: 1 }
     */
    channel_status?: SkillStatusEffect;
  };
  successful_target_ids?: number[]; // For global skills, the target ids that were successful TODO: move this

  // ========== Advanced Skill Mechanics ==========

  /**
   * If true, this skill starts battle ready to use (not on cooldown)
   * By default all skills start on cooldown
   */
  starts_active?: boolean;

  /**
   * @deprecated Use event_checks instead for more flexible condition/effect logic
   * Finisher mechanic: bonus effects when target HP is below threshold
   */
  finisher?: SkillFinisher;

  /**
   * Generic event checks: arrays of conditions and effects
   * When all conditions in a group are met, all effects in that group are applied
   */
  event_checks?: SkillEventCheck[];

  /**
   * Momentum mechanic: stacking effects that grow/diminish with each use
   */
  momentum?: SkillMomentum;

  /**
   * Reactive triggers: events that can reset cooldown or trigger effects
   */
  reactive_triggers?: ReactiveTriggerConfig[];

  /**
   * Team-based conditions: bonuses based on team state
   */
  team_conditions?: SkillTeamCondition[];

  /**
   * Telegraph AoE: where circle/line patterns resolve anchors at cast time.
   * `caster` uses static on_cast; other modes expand at useSkill.
   */
  telegraph_anchor?: "caster" | "random_regions" | "all_enemies";

  /** Warn-phase duration before hit tiles fire (telegraph skills). */
  telegraph_ticks?: number;
}

export interface Battle_Action {
  type: "MOVE" | "USE_SKILL" | "TURN" | "FAINT" | "DANCE";
  total_ticks: number;
  remaining_ticks: number;
  log_info: Record<string, any>;
  starting_tick: number;
}

export interface Battle_Action_Turn extends Battle_Action {
  type: "TURN";
  to: Direction;
}

export interface Battle_Action_Move extends Battle_Action {
  type: "MOVE";
  to: Position;
}

export interface Battle_Action_UseSkill extends Battle_Action {
  type: "USE_SKILL";
  skill: SkillData;
  target_id?: number;
}

export interface Battle_Action_Faint extends Battle_Action {
  type: "FAINT";
}

export interface Battle_Action_Dance extends Battle_Action {
  type: "DANCE";
}

export enum Direction {
  S = `South`,
  SE = `SouthEast`,
  E = `East`,
  NE = `NorthEast`,
  N = `North`,
  NW = `NorthWest`,
  W = `West`,
  SW = `SouthWest`,
}

export const DirectionArray = [
  Direction.S,
  Direction.SE,
  Direction.E,
  Direction.NE,
  Direction.N,
  Direction.NW,
  Direction.W,
  Direction.SW,
];

export interface PokemonPremierAura {
  active: boolean;
  colors: number[];
}

export interface BattlePokemonData {
  id: number;
  base_pokemon_name: string;
  name: string;
  level: number;
  pokemon_types: PokemonType[];
  hp: number;
  max_hp: number;
  ability_power: number;
  resistance: number;
  speed: number;
  weight: number; // Pokemon weight in hectograms
  skills: SkillData[];
  premier_aura?: PokemonPremierAura;
  is_shiny?: boolean;
  scale?: number;
  is_dummy?: boolean;
  /** Siege elite — client shows badge left of name. */
  is_elite?: boolean;
}

export interface SkillGlobalEventCheck {
  __typename:
  | `AlwaysOnCertainTypes`
  | `AllButCertainTypes`
  | `OnlyCertainTypes`
  | `TargetHasBuff`
  | `TargetHasDebuff`
  | `TargetBelowHpPercent`
  | `CasterBelowHpPercent`
  | `CasterAboveHpPercent`
  | `TargetHasStatus`;
  pokemon_types?: PokemonType[];
  hp_percent?: number; // For HP-based checks (0-100)
  status_effect?: StatusEffect; // For status-based checks
}

// ========== Advanced Skill Mechanics ==========

/**
 * Status effect with flexible targeting for finisher/momentum effects
 */
export interface TargetedStatusEffect {
  effect: SkillStatusEffect;
  target:
  | "self"
  | "target"
  | "random_ally"
  | "random_enemy"
  | "all_allies"
  | "all_enemies";
}

/**
 * Finisher mechanic: bonus effects when target is below HP threshold
 */
export interface SkillFinisher {
  threshold_hp_percent: number; // 0-100, e.g. 30 = below 30% HP
  damage_multiplier?: number; // e.g. 1.5 = 50% bonus damage
  status_effects?: TargetedStatusEffect[];
}

/**
 * Momentum effect types
 */
export type MomentumEffectType = "damage_dealt_mult" | "cooldown_mult";

/**
 * Individual momentum effect
 */
export interface MomentumEffect {
  type: MomentumEffectType;
  value_per_stack: number; // e.g. 0.1 = +10% per stack, -0.1 = -10% per stack
}

/**
 * Momentum mechanic: stack-based effects that grow/diminish with use
 */
export interface SkillMomentum {
  max_stacks: number;
  effects: MomentumEffect[];
}

/**
 * Team condition types for team_conditions
 */
export type TeamConditionType =
  | "last_alive"
  | "all_alive"
  | "min_ally_type_count"
  | "min_enemy_type_count"
  | "ally_count_gte"
  | "ally_count_lte";

/**
 * Team condition configuration
 */
export interface TeamCondition {
  type: TeamConditionType;
  value?: number; // For count-based conditions
  pokemon_type?: PokemonType; // For type-based conditions
}

/**
 * Team condition effect types
 */
export type TeamConditionEffectStat =
  | "damage_mult"
  | "shield_mult"
  | "crit_chance_flat"
  | "cooldown_recharge"
  | "healing_mult"
  | "apply_status"
  | "cleanse";

/**
 * Team condition effect
 */
export interface TeamConditionEffect {
  stat: TeamConditionEffectStat;
  value: number; // e.g. 1.5 = 50% bonus, 0.2 = flat 20%
  status_effects?: TargetedStatusEffect[];
}

/**
 * Full team condition with condition and effect
 */
export interface SkillTeamCondition {
  condition: TeamCondition;
  effect: TeamConditionEffect;
}

// ========== Generic Event Check System ==========

/**
 * Condition types for skill event checks
 */
export type SkillConditionType =
  | "CASTER_HP_GT"
  | "CASTER_HP_LT"
  | "TARGET_HP_GT"
  | "TARGET_HP_LT"
  | "CASTER_WEIGHT_GT"
  | "CASTER_WEIGHT_LT"
  | "TARGET_WEIGHT_GT"
  | "TARGET_WEIGHT_LT"
  | "CASTER_HAS_STATUS"
  | "TARGET_HAS_STATUS"
  | "CASTER_MISSING_STATUS"
  | "TARGET_MISSING_STATUS"
  | "TARGET_IS_TYPE"
  | "CHANCE";

/**
 * A single condition for a skill event check
 */
export interface SkillCondition {
  type: SkillConditionType;
  value?: number; // HP percent (0-100), weight threshold, or chance (0-1)
  status?: StatusEffect; // For status-based conditions
  pokemon_type?: PokemonType; // For type-based conditions
}

/**
 * Effect types for skill event checks
 */
export type SkillEffectType =
  | "DAMAGE_MULTIPLIER"
  | "HEAL_FIXED"
  | "HEAL_PERCENT_DAMAGE"
  | "HEAL_ALL_ALLIES_FIXED"
  | "HEAL_ALL_ALLIES_PERCENT_DAMAGE"
  | "COOLDOWN_RECHARGE"
  | "APPLY_STATUS_SELF"
  | "APPLY_STATUS_TARGET"
  | "APPLY_STATUS_ALL_ALLIES"
  | "APPLY_STATUS_ALL_ENEMIES"
  | "CLEANSE_SELF"
  | "CLEANSE_TARGET"
  | "CLEANSE_ALL_ALLIES"
  | "CLEANSE_ALL_ENEMIES";

/**
 * Cleanse configuration for effect
 */
export interface CleanseConfig {
  count: number; // 0 = all
  category?: "buff" | "debuff" | "all";
  specific_statuses?: StatusEffect[];
}

/**
 * A single effect to apply when conditions are met
 */
export interface SkillEffect {
  type: SkillEffectType;
  value?: number; // Multiplier, heal amount, or cooldown percent (0-1)
  status_effects?: SkillStatusEffect[]; // For status application
  cleanse_config?: CleanseConfig; // For cleanse effects
}

/**
 * Generic event check: conditions + effects
 * All conditions must be met for effects to apply
 */
export interface SkillEventCheck {
  conditions: SkillCondition[];
  effects: SkillEffect[];
}
